import { FC, useContext, useEffect, useState } from "react";
import { FormProps } from ".";
import Button from "../common/button";
import { AuthContext, UserPayload } from "@/context/AuthContext";
import React from "react";

import { registerFormEN } from "@/assets/dictionaries/en/sign-in-content";
import { registerFormES } from "@/assets/dictionaries/es/sign-in-content";

enum Alerts {
  NO_ALERT,
  USER_ALREADY_EXISTS,
  WRONG,
  SERVER_ERROR,
}
const SERVER_API_URL = process.env.NEXT_PUBLIC_SERVER_API_URL;

const RegisterForm: FC<FormProps> = ({ changeForm, onSuccess }) => {
  const { logIn } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    phone_country_zone: "+1",
    phone_number: "",
    timezone: "",
    avatar_url: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    phone_country_zone: "",
    phone_number: "",
    timezone: "",
    avatar_url: "",
  });

  const [formDataSecret, setFormDataSecret] = useState({
    verification_code: "",
  });
  const [formErrorsSecret, setFormErrorsSecret] = useState({
    verification_code: "",
  });
  const [showTokenfield, setShowTokenfield] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState(Alerts.NO_ALERT);

  const [registerFormContent, setRegisterFormContent] =
    useState(registerFormEN); // Default to English

  useEffect(() => {
    // Get the language from localStorage
    const lang = localStorage.getItem("selectedOption") || "EN"; // Default to EN if not found

    // Set the content based on the language
    switch (lang.toUpperCase()) {
      case "ES":
        setRegisterFormContent(registerFormES);
        break;
      case "EN":
      default:
        setRegisterFormContent(registerFormEN);
        break;
    }
  }, []); // Only run on mount

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    // setFormData({ ...formData, [e.target.id]: e.target.value });
    const { id, value } = e.target;

    // Only allow numbers in the phone field
    if (id === "phone_number") {
      const numericValue = value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
      setFormData({ ...formData, [id]: numericValue });
    } else {
      setFormData({ ...formData, [id]: value });
    }
    setFormErrors({ ...formErrors, [e.target.id]: "" }); // Remove error on type
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    // setFormData({ ...formData, [e.target.id]: e.target.value });
    const { id, value } = e.target;

    setFormData({ ...formData, [id]: value });
    setFormErrors({ ...formErrors, [e.target.id]: "" }); // Remove error on type
  };

  const handleChangeSecret = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setFormDataSecret({ ...formDataSecret, [e.target.id]: e.target.value });
    setFormErrorsSecret({ ...formErrorsSecret, [e.target.id]: "" }); // Remove error on type
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = {
      name: "",
      last_name: "",
      email: "",
      password: "",
      confirm_password: "",
      phone_country_zone: "",
      phone_number: "",
      timezone: "",
      avatar_url: "",
    };

    let register_errors = 0;

    if (!formData.name) {
      errors.name = registerFormContent.error_name;
      register_errors = register_errors + 1;
    }

    if (!formData.last_name) {
      errors.last_name = registerFormContent.error_last_name;
      register_errors = register_errors + 1;
    }

    if (!formData.email) {
      errors.email = registerFormContent.error_email_1;
      register_errors = register_errors + 1;
    } else {
      if (!formData.email.match(/^[\w-.]+(\+[\w-]*)?@([\w-]+\.)+[\w-]{2,4}$/)) {
        errors.email = registerFormContent.error_email_2;
        register_errors = register_errors + 1;
      }
    }

    if (!formData.password) {
      errors.password = registerFormContent.error_password_1;
      register_errors = register_errors + 1;
    } else if (formData.password.length < 8) {
      errors.password = registerFormContent.error_password_2;
      register_errors = register_errors + 1;
    } else if (!formData.password.match(/[a-z]/)) {
      errors.password = registerFormContent.error_password_3;
      register_errors = register_errors + 1;
    } else if (!formData.password.match(/[A-Z]/)) {
      errors.password = registerFormContent.error_password_4;
      register_errors = register_errors + 1;
    } else if (!formData.password.match(/[0-9]/)) {
      errors.password = registerFormContent.error_password_5;
      register_errors = register_errors + 1;
    } else if (!formData.password.match(/[@$!?#%*?&]/)) {
      errors.password = registerFormContent.error_password_6;
      register_errors = register_errors + 1;
    }

    if (!formData.confirm_password) {
      errors.confirm_password = registerFormContent.error_confirm_password_1;
      register_errors = register_errors + 1;
    } else if (formData.confirm_password != formData.password) {
      errors.confirm_password = registerFormContent.error_confirm_password_2;
      register_errors = register_errors + 1;
    }

    if (!formData.phone_number) {
      errors.phone_number = registerFormContent.error_phone_number_1;
      register_errors = register_errors + 1;
    } else if (formData.phone_number.length < 10) {
      errors.phone_number = registerFormContent.error_phone_number_2;
      register_errors = register_errors + 1;
    }

    if (register_errors != 0) {
      setFormErrors(errors);
      return;
    }

    setDisableButton(true);

    const credentials = {
      email: formData.email,
      password: formData.password,
      attributes: {
        name: formData.name,
        last_name: formData.last_name,
        phone_country_zone: formData.phone_country_zone,
        phone_number: formData.phone_number,
        timezone: formData.timezone,
        avatar_url: formData.avatar_url,
      },
    };

    const registerUser = async () => {
      try {
        const res = await fetch(`${SERVER_API_URL}/users/`, {
          body: JSON.stringify(credentials),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        });

        if (res.status >= 200 && res.status <= 299) {
          const data = await res.json();
          setDisableButton(false);
          setShowTokenfield(true);
          setShowAlert(Alerts.NO_ALERT);
          // onSuccess();
        } else {
          const error = await res.json();
          const detail: string = error["detail"];
          setDisableButton(false);
          if (detail.includes("User") && res.status == 500) {
            setShowAlert(Alerts.USER_ALREADY_EXISTS);
          } else if (res.status == 400) {
            setShowAlert(Alerts.WRONG);
          } else {
            setShowAlert(Alerts.SERVER_ERROR);
          }
        }
      } catch (e) {
        setShowAlert(Alerts.SERVER_ERROR);
        setDisableButton(false);
      }
    };

    registerUser();
  };

  const handleSubmitVerify = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = {
      email: "",
      verification_code: "",
    };

    let register_errors = 0;

    if (!formData.email) {
      errors.email = registerFormContent.error_email_1;
      register_errors = register_errors + 1;
    } else {
      if (!formData.email.match(/^[\w-.]+(\+[\w-]*)?@([\w-]+\.)+[\w-]{2,4}$/)) {
        errors.email = registerFormContent.error_email_2;
        register_errors = register_errors + 1;
      }
    }

    if (!formDataSecret.verification_code) {
      errors.verification_code = registerFormContent.error_verification;
      register_errors = register_errors + 1;
    }

    if (register_errors != 0) {
      setFormErrorsSecret(errors);
      return;
    }

    setDisableButton(true);

    const credentials = {
      email: formData.email,
      verification_code: formDataSecret.verification_code,
    };

    const verifyUser = async () => {
      try {
        const res = await fetch(`${SERVER_API_URL}/users/verify/user`, {
          body: JSON.stringify(credentials),
          headers: {
            "Content-Type": "application/json",
          },
          method: "PUT",
        });

        if (res.status >= 200 && res.status <= 299) {
          const data = await res.json();
          setDisableButton(false);
          onSuccess();
        } else {
          const error = await res.json();
          const detail: string = error["detail"];
          setDisableButton(false);
          if (detail.includes("User") && res.status == 500) {
            setShowAlert(Alerts.USER_ALREADY_EXISTS);
          } else if (res.status == 400) {
            setShowAlert(Alerts.WRONG);
          } else {
            setShowAlert(Alerts.SERVER_ERROR);
          }
        }
      } catch (e) {
        setShowAlert(Alerts.SERVER_ERROR);
        setDisableButton(false);
      }
    };

    verifyUser();
  };

  return (
    <div>
      {showTokenfield ? (
        <>
          <p className="text-3xl font-bold text-center">
            {registerFormContent.verify_account_title}
          </p>
          <p className="mt-2 text-xl text-slate-400 mb-8 text-center">
            {registerFormContent.verify_account_desc}
          </p>

          <form
            className="w-full flex flex-col gap-2"
            onSubmit={handleSubmitVerify}
          >
            <label className="text-slate-400 font-bold flex flex-col">
              {registerFormContent.verification_code_label}
              <input
                className="border-2 rounded-lg p-2 w-full"
                placeholder={registerFormContent.verification_code}
                type="text"
                id="verification_code"
                value={formDataSecret.verification_code}
                onChange={handleChangeSecret}
                autoFocus={true}
              />
              <p
                className={
                  formErrorsSecret.verification_code
                    ? "text-red-500"
                    : "text-transparent select-none"
                }
              >
                {formErrorsSecret.verification_code || "z"}
              </p>
            </label>
            <Button size="large" type="submit" disabled={disableButton}>
              {registerFormContent.button_verify}
            </Button>
          </form>
        </>
      ) : (
        <>
          <p className="text-3xl font-bold text-center">
            {registerFormContent.register_title}
          </p>
          <p className="mt-2 text-xl text-slate-400 mb-8 text-center">
            {registerFormContent.register_desc_1}
            <button
              onClick={() => changeForm("login")}
              className=" text-gbci-accent font-bold"
            >
              {registerFormContent.register_desc_2}
            </button>{" "}
            {registerFormContent.register_desc_3}
          </p>

          <form className="w-full flex flex-col gap-2" onSubmit={handleSubmit}>
            <label className="text-slate-400 font-bold flex flex-col">
              {registerFormContent.register_name_label}
              <input
                className="border-2 rounded-lg p-2 w-full"
                placeholder={registerFormContent.register_name}
                type="text"
                id="name"
                value={formData.name}
                onChange={handleChange}
                autoFocus={true}
              />
              <p
                className={
                  formErrors.name
                    ? "text-red-500"
                    : "text-transparent select-none"
                }
              >
                {formErrors.name || "z"}
              </p>
            </label>

            <label className="text-slate-400 font-bold flex flex-col">
              {registerFormContent.register_last_name_label}
              <input
                className="border-2 rounded-lg p-2 w-full"
                placeholder={registerFormContent.register_last_name}
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
              <p
                className={
                  formErrors.last_name
                    ? "text-red-500"
                    : "text-transparent select-none"
                }
              >
                {formErrors.last_name || "z"}
              </p>
            </label>

            <label className="text-slate-400 font-bold flex flex-col">
              {registerFormContent.register_phone_number_label}
              <div className="w-full flex">
                <select
                  className="border-2 rounded-lg p-2"
                  id="phone_country_zone"
                  value={formData.phone_country_zone}
                  onChange={handleChangeSelect}
                >
                  <option value="+1">+1 (US)</option>
                  <option value="+52">+52 (MXN)</option>
                </select>
                <input
                  className="border-2 rounded-lg p-2 w-full"
                  placeholder={registerFormContent.register_phone_number}
                  type="tel"
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  pattern="[0-9]*"
                  inputMode="numeric"
                />
              </div>
              <p
                className={
                  formErrors.phone_number
                    ? "text-red-500"
                    : "text-transparent select-none"
                }
              >
                {formErrors.phone_number || "z"}
              </p>
            </label>

            <label className="text-slate-400 font-bold flex flex-col">
              {registerFormContent.register_email_label}
              <input
                className="border-2 rounded-lg p-2 w-full"
                placeholder={registerFormContent.register_email}
                type="text"
                id="email"
                value={formData.email}
                onChange={handleChange}
              />
              <p
                className={
                  formErrors.email
                    ? "text-red-500"
                    : "text-transparent select-none"
                }
              >
                {formErrors.email || "z"}
              </p>
            </label>

            <label className="text-slate-400 font-bold flex flex-col">
              {registerFormContent.register_password_label}
              <input
                type={showPassword ? "text" : "password"}
                className="border-2 rounded-lg p-2 w-full"
                placeholder={registerFormContent.register_password}
                id="password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="inset-y-0 right-0 px-1 flex items-center focus:outline-none"
              >
                {showPassword
                  ? registerFormContent.hide_password
                  : registerFormContent.show_password}
              </button>
              <p
                className={
                  formErrors.password
                    ? "text-red-500"
                    : "text-transparent select-none"
                }
              >
                {formErrors.password || "z"}
              </p>
            </label>

            <label className="text-slate-400 font-bold flex flex-col">
              {registerFormContent.register_confirm_password_label}
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="border-2 rounded-lg p-2 w-full"
                placeholder={registerFormContent.register_confirm_password}
                id="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="inset-y-0 right-0 px-1 flex items-center focus:outline-none"
              >
                {showConfirmPassword
                  ? registerFormContent.hide_password
                  : registerFormContent.show_password}
              </button>
              <p
                className={
                  formErrors.confirm_password
                    ? "text-red-500"
                    : "text-transparent select-none"
                }
              >
                {formErrors.confirm_password || "z"}
              </p>
            </label>

            <div className="flex justify-between flex-col-reverse md:flex-row gap-6">
              <div className=" text-slate-400 font-bold text-right mb-4">
                {registerFormContent.verify_desc_1}
                <button
                  className="text-gbci-accent"
                  onClick={() => changeForm("resend")}
                >
                  {registerFormContent.verify_desc_2}
                </button>
              </div>
            </div>

            <Button size="large" type="submit" disabled={disableButton}>
              {registerFormContent.button_create}
            </Button>
          </form>
        </>
      )}
      {showAlert !== Alerts.NO_ALERT && (
        <div className="mt-8 p-4 text-red-400 bg-red-100 border border-red-400 rounded-md">
          <p>
            {showAlert == Alerts.USER_ALREADY_EXISTS
              ? registerFormContent.alert_1
              : showTokenfield
                ? showAlert == Alerts.WRONG
                  ? registerFormContent.alert_2
                  : registerFormContent.alert_3
                : registerFormContent.alert_4}
          </p>
        </div>
      )}
    </div>
  );
};

export default RegisterForm;
